import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  LayoutGrid, 
  CheckCircle, 
  Play, 
  Code2, 
  FileCode,
  Maximize2, 
  Minimize2,
  Zap
} from 'lucide-react';
import InteractiveGraphEditor from '@/components/GraphEditor';
import ValidationPanel from '@/components/ValidationPanel';
import SimulationPanel from '@/components/SimulationPanel';
import ExportPanel from '@/components/ExportPanel';
import DSLViewer from '@/components/DSLViewer';
import CapabilitiesEffectsPanel from '@/components/CapabilitiesEffectsPanel';
import { cn } from '@/lib/utils';

/**
 * Center Surface - Graph Workbench (Primary Authority)
 * 
 * This is the only place machines are authored visually.
 * The graph is no longer "one of many views" - it is the PRIMARY construction surface.
 * 
 * Tabs:
 * - Graph (PRIMARY) - Visual authoring surface
 * - Caps & Effects - Authority management (first-class)
 * - Simulate - Controlled execution
 * - Export - Compilation ceremony
 * - DSL (Advanced) - Textual projection
 */
const CenterSurface = ({
  machine,
  machineId,
  validation,
  capabilities,
  onNodeSelect,
  onRevalidate,
  onMachineUpdate,
  onDSLEdit,
  focusMode,
  onFocusModeToggle,
  onSimulationTraceUpdate
}) => {
  const [mode, setMode] = useState('graph');
  const [highlightedState, setHighlightedState] = useState(null);

  const modes = [
    { id: 'graph', label: 'Graph', icon: LayoutGrid, primary: true },
    { id: 'caps-effects', label: 'Caps & Effects', icon: Zap, primary: false },
    { id: 'simulate', label: 'Simulate', icon: Play, primary: false },
    { id: 'export', label: 'Export', icon: Code2, primary: false },
    { id: 'dsl', label: 'DSL', icon: FileCode, primary: false, advanced: true },
  ];

  const handleHighlightState = (stateId) => {
    setHighlightedState(stateId);
    setMode('graph');
    onNodeSelect?.(stateId);
  };

  return (
    <div className="h-full flex flex-col" data-testid="center-surface">
      {/* Mode Bar - Desktop Segmented Control */}
      <div className="border-b border-white/10 px-3 py-2 bg-black/20 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-2">
          {/* Desktop Segmented Control */}
          <div className="flex items-center space-x-1 bg-black/30 rounded p-1">
            {modes.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded transition-all flex items-center space-x-1.5",
                    mode === m.id
                      ? m.primary 
                        ? "bg-blue-500/30 text-blue-300 ring-1 ring-blue-500/50" 
                        : m.id === 'caps-effects'
                          ? "bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/50"
                          : "bg-blue-500/20 text-blue-400"
                      : m.advanced
                        ? "text-gray-500 hover:text-gray-400 hover:bg-white/5"
                        : "text-gray-400 hover:text-gray-300 hover:bg-white/5"
                  )}
                  data-testid={`mode-${m.id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{m.label}</span>
                  {m.advanced && (
                    <span className="text-[8px] text-gray-600 ml-0.5">(ADV)</span>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Mode indicators */}
          {mode === 'graph' && (
            <span className="text-[10px] text-green-500/70 font-medium ml-2">
              PRIMARY
            </span>
          )}
          {mode === 'caps-effects' && (
            <span className="text-[10px] text-yellow-500/70 font-medium ml-2">
              AUTHORITY
            </span>
          )}
          {mode === 'dsl' && (
            <span className="text-[10px] text-gray-500 font-medium ml-2">
              TEXTUAL PROJECTION
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Focus Mode Toggle - Only in Graph mode */}
          {mode === 'graph' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onFocusModeToggle}
              className="h-7 text-xs"
              data-testid="focus-mode-toggle"
            >
              {focusMode ? (
                <>
                  <Minimize2 className="w-3.5 h-3.5" />
                  <span className="ml-1.5">Exit Focus (F)</span>
                </>
              ) : (
                <>
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span className="ml-1.5">Focus Mode (F)</span>
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {mode === 'graph' && (
          <InteractiveGraphEditor
            machine={machine}
            machineId={machineId}
            onNodeSelect={onNodeSelect}
            onMachineUpdate={onMachineUpdate}
            onTraceUpdate={onSimulationTraceUpdate}
            readOnly={false}
            highlightedState={highlightedState}
          />
        )}
        {mode === 'caps-effects' && (
          <CapabilitiesEffectsPanel
            machine={machine}
            runtimeProfile="simulation"
            onMachineUpdate={onMachineUpdate}
            onHighlightState={handleHighlightState}
          />
        )}
        {mode === 'dsl' && (
          <DSLViewer
            machine={machine}
            machineId={machineId}
            onDSLEdit={onDSLEdit}
          />
        )}
        {mode === 'validate' && (
          <div className="h-full overflow-auto p-4">
            <ValidationPanel
              machineId={machineId}
              validation={validation}
              onRevalidate={onRevalidate}
            />
          </div>
        )}
        {mode === 'simulate' && (
          <div className="h-full overflow-auto p-4">
            <SimulationPanel
              machineId={machineId}
              machine={machine}
              onTraceUpdate={onSimulationTraceUpdate}
            />
          </div>
        )}
        {mode === 'export' && (
          <div className="h-full overflow-auto p-4">
            <ExportPanel
              machineId={machineId}
              machine={machine}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CenterSurface;
